import React, { useContext, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { Transition } from 'react-transition-group';
import {
  Onboarding,
  OnboardingContext,
  OnboardingSubtitle,
  OnboardingTitle as OnboardingTitleBase,
} from 'registration/containers/Onboarding';
import { ensureMapNurseSpecialties } from 'registration/helpers/nurseSpecialties';
import { NurseOnboardingOptions } from 'registration/types';

import { Input } from 'shared/components/ihcl/input';
import { Center } from 'shared/components/ihcl/positioning';
import {
  styled,
  Theme,
  useStyletron,
  withStyle,
} from 'shared/components/ihcl/styled';
import { Tag } from 'shared/components/ihcl/tag';
import { ButtonSelect } from 'shared/components/ihcl/v2/button-select';
import { noYesOptions } from 'shared/constants/selectOptions';

import isReactivationFlow from '../../helpers/isReactivationFlow';

const SPECIALTY_YEARS_MIN = 1;
const SPECIALTY_YEARS_MAX = 99;

const yearInputMargin = '16px';
const yearInputWidth = '80px';
const specialtyRowHeight = '50px';

const OnboardingTitle = withStyle(
  OnboardingTitleBase,
  ({
    $additionalTopSpacing = false,
  }: {
    $additionalTopSpacing: boolean;
    $theme: Theme;
  }) => ({
    marginTop: $additionalTopSpacing ? '40px' : '64px',
  })
);

const SpecialtyRow = styled('div', {
  height: specialtyRowHeight,
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
});

const SpecialtyButtonWrapper = styled<'div', { $isFullWidth: boolean }>(
  'div',
  ({ $isFullWidth }) => ({
    display: 'flex',
    alignItems: 'center',
    height: specialtyRowHeight,
    width: $isFullWidth
      ? '100%'
      : `calc(100% - ${yearInputWidth} - ${yearInputMargin})`,
    position: 'absolute',
    transition: 'transform 200ms',
  })
);

const SpecialtyDetails = styled<'div', { $isVisible: boolean }>(
  'div',
  ({ $isVisible }) => ({
    display: 'inline-block',
    height: specialtyRowHeight,
    width: yearInputWidth,
    right: 0,
    top: '7px',
    position: 'absolute',
    transition: 'transform 200ms',
    transform: $isVisible
      ? `translateX(calc(100% - ${yearInputWidth}))`
      : 'translateX(0)',
  })
);

const isValidSpecialtyYears = (years, recentGrad) =>
  years === undefined ||
  years === '' ||
  (SPECIALTY_YEARS_MIN <= years && years <= SPECIALTY_YEARS_MAX) ||
  (recentGrad && years === 0);

const VerifyHasWorkExperience = ({
  nextActionForRecentGrads,
  setHasWorkExperience,
}) => {
  const { history } = useContext(OnboardingContext);
  return (
    <>
      <OnboardingTitle>
        Have you worked as an RN after graduation?
      </OnboardingTitle>
      <ButtonSelect
        className="justify-center"
        options={noYesOptions}
        selectionMode="single"
        values={[]}
        onValuesChange={([optionValue]) => {
          if (optionValue === false) {
            if (history && history.push) {
              history.push(nextActionForRecentGrads);
            } else {
              window.mixpanel.track(
                'No history.push available, falling-back to window.location'
              );
              window.location = nextActionForRecentGrads;
            }
            return;
          }
          setHasWorkExperience(true);
        }}
      />
    </>
  );
};

VerifyHasWorkExperience.propTypes = {
  nextActionForRecentGrads: PropTypes.string.isRequired,
  setHasWorkExperience: PropTypes.func.isRequired,
};

const OnboardingSpecialtiesList = ({
  hideRecentGradYearEntry = false,
  isReactivation = false,
  nextAction = null,
  nextActionForRecentGrads = null,
  previousAction = null,
  stepNumber,
}) => {
  const {
    onboardingBasePath,
    onboardingData,
    onboardingOptions,
    onboardingUpdates,
    setOnboardingData,
  } = useContext(OnboardingContext);
  const [, theme] = useStyletron();
  const [allowNoSpecialties, setAllowNoSpecialties] = useState(false);
  const [specialtyYearsValid, setSpecialtyYearsValid] = useState({});
  const [recentGradHasWorkExperience, setRecentGradHasWorkExperience] =
    useState(null);

  const initiallyPopulatedSpecialtyIds = useMemo(() => {
    if (onboardingData?.nurseSpecialties) {
      // @ts-ignore
      return new Set(onboardingData.nurseSpecialties.keys());
    }
    return new Set<number>();
  }, []);
  const updateRecentGradFields = () => {
    if (recentGradHasWorkExperience) {
      const newNurseSpecialties = new Map<number, string | number>();
      onboardingData.nurseSpecialties.forEach((years, id) => {
        newNurseSpecialties.set(id, years || 1);
      });
      setOnboardingData({
        ...onboardingData,
        nurseSpecialties: newNurseSpecialties,
        recent_grad: false,
      });
    }
  };
  const wrappedSetRecentGradHasWorkExperience = (
    newRecentGradHasWorkExperience
  ) => {
    // When setting recentGradHasWorkExperience to true, update the related fields
    if (!recentGradHasWorkExperience && newRecentGradHasWorkExperience) {
      updateRecentGradFields();
    }
    setRecentGradHasWorkExperience(newRecentGradHasWorkExperience);
  };

  if (!onboardingData || Object.keys(onboardingData).length < 1) {
    return null;
  }

  const { nurse_specialty_descriptions: nurseSpecialtyDescriptions } =
    onboardingOptions as NurseOnboardingOptions;

  const sortedNurseSpecialtyDescriptions = [...nurseSpecialtyDescriptions];

  if (initiallyPopulatedSpecialtyIds) {
    sortedNurseSpecialtyDescriptions.sort(
      (a, b) =>
        Number(initiallyPopulatedSpecialtyIds.has(b.id)) -
        Number(initiallyPopulatedSpecialtyIds.has(a.id))
    );
  }

  const { nurseSpecialties: ambigiousNurseSpecialties } = onboardingData;

  // HACK: Force nurseSpecialties to be typescript map
  let nurseSpecialties = ensureMapNurseSpecialties(ambigiousNurseSpecialties);

  const { recent_grad: recentGradOD } = onboardingData;
  const { recent_grad: recentGradOU } = onboardingUpdates;
  const recentGrad = recentGradOD || recentGradOU;
  if (nurseSpecialties.size === 0 && !allowNoSpecialties) {
    nurseSpecialties = new Map().set(undefined, undefined);
  }

  const specialtyNeedsId = Array.from(nurseSpecialties).some(
    ([id]) => id === undefined
  );

  const specialtyNeedsYears = Array.from(nurseSpecialties).some(
    ([, years]) => years === undefined || years === ''
  );

  const specialtyInvalidYears = Array.from(nurseSpecialties).some(
    ([, years]) =>
      !isValidSpecialtyYears(years, recentGrad && hideRecentGradYearEntry)
  );

  const specialtyNeedsValues =
    specialtyNeedsId || specialtyNeedsYears || specialtyInvalidYears;

  const nurseSpecialtiesArr = Array.from(nurseSpecialties);

  const shouldUpdateRecentGrad = isReactivationFlow(onboardingBasePath);
  const title = isReactivationFlow(onboardingBasePath) ? (
    <OnboardingTitle>
      {recentGradHasWorkExperience
        ? 'What specialties have you worked in?'
        : 'Have you gained any new experience?'}
    </OnboardingTitle>
  ) : (
    <span>
      <OnboardingTitle>
        {recentGrad && hideRecentGradYearEntry
          ? 'Select specialties you want to work in!'
          : 'Select specialties you have experience in!'}
      </OnboardingTitle>
      <OnboardingSubtitle>
        {recentGrad && hideRecentGradYearEntry
          ? 'Although not all hospitals have these open for recent graduates, this still helps them place you.'
          : 'It’s ok to estimate your years of experience. More specialties = more job matches!'}
      </OnboardingSubtitle>
    </span>
  );

  let contents;
  if (isReactivation && recentGrad && recentGradHasWorkExperience === null) {
    contents = (
      <VerifyHasWorkExperience
        nextActionForRecentGrads={nextActionForRecentGrads}
        setHasWorkExperience={wrappedSetRecentGradHasWorkExperience}
      />
    );
  } else {
    contents = (
      <Center>
        {title}
        {sortedNurseSpecialtyDescriptions.map((nsd) => (
          <SpecialtyRow key={nsd.id}>
            <Transition
              in={!(Number(nurseSpecialties.get(nsd.id)) >= 0)}
              timeout={200}
            >
              {(state) => (
                <SpecialtyButtonWrapper
                  $isFullWidth={
                    ['entering', 'entered'].includes(state) ||
                    recentGrad ||
                    recentGradHasWorkExperience
                  }
                >
                  <Tag
                    closeable={false}
                    role="option"
                    aria-selected={Number(nurseSpecialties.get(nsd.id)) >= 0}
                    variant="solid"
                    size="medium"
                    kind={
                      Number(nurseSpecialties.get(nsd.id)) >= 0
                        ? 'custom'
                        : 'primary'
                    }
                    color={theme.colors.primary}
                    $textColor={
                      Number(nurseSpecialties.get(nsd.id)) >= 0
                        ? 'white'
                        : theme.colors.primary
                    }
                    $containerWidth
                    onClick={() => {
                      const newNurseSpecialties = new Map();
                      let isRecentGrad = onboardingData.recent_grad;
                      nurseSpecialties.forEach((years, id) => {
                        if (id && id !== nsd.id) {
                          newNurseSpecialties.set(id, years);
                        }
                      });
                      if (!(Number(nurseSpecialties.get(nsd.id)) >= 0)) {
                        if (recentGrad || recentGradHasWorkExperience) {
                          if (isReactivation) {
                            newNurseSpecialties.set(nsd.id, 1);
                            if (shouldUpdateRecentGrad) {
                              isRecentGrad = false;
                            }
                          } else {
                            newNurseSpecialties.set(nsd.id, 0);
                          }
                        } else {
                          newNurseSpecialties.set(nsd.id, '');
                        }
                      }
                      setOnboardingData({
                        ...onboardingData,
                        nurseSpecialties: newNurseSpecialties,
                        recent_grad: isRecentGrad,
                      });
                    }}
                  >
                    {nsd.description}
                  </Tag>
                </SpecialtyButtonWrapper>
              )}
            </Transition>
            <Transition
              in={Number(nurseSpecialties.get(nsd.id)) >= 0}
              timeout={200}
            >
              {(state) =>
                (recentGrad || recentGradHasWorkExperience) &&
                hideRecentGradYearEntry ? null : (
                  <SpecialtyDetails
                    $isVisible={['entering', 'entered'].includes(state)}
                  >
                    {['entering', 'entered'].includes(state) && (
                      <Input
                        data-testid={`specialty-years-input-${nsd.id}`}
                        autoFocus
                        size="tiny"
                        label="Years"
                        tinyLabel="yrs"
                        type="number"
                        inputMode="decimal"
                        min="{SPECIALTY_YEARS_MIN}"
                        max="{SPECIALTY_YEARS_MAX}"
                        step="1"
                        value={nurseSpecialties.get(nsd.id)}
                        error={
                          nsd.id in specialtyYearsValid &&
                          !specialtyYearsValid[nsd.id]
                        }
                        onChange={(evt) => {
                          const newNurseSpecialties = new Map(nurseSpecialties);
                          newNurseSpecialties.set(
                            nsd.id,
                            evt.currentTarget.value
                          );
                          const newOnboardingData = {
                            ...onboardingData,
                            nurseSpecialties: newNurseSpecialties,
                          };
                          if (
                            recentGrad &&
                            shouldUpdateRecentGrad &&
                            isValidSpecialtyYears(
                              evt.currentTarget.value,
                              recentGrad
                            )
                          ) {
                            newOnboardingData.recent_grad = false;
                            newOnboardingData.years_experience =
                              evt.currentTarget.value;
                          }
                          setOnboardingData(newOnboardingData);
                          setSpecialtyYearsValid({
                            ...specialtyYearsValid,
                            [nsd.id]: isValidSpecialtyYears(
                              evt.currentTarget.value,
                              recentGrad
                            ),
                          });
                        }}
                        style={{
                          maxWidth: yearInputWidth,
                          minWidth: yearInputWidth,
                        }}
                      />
                    )}
                  </SpecialtyDetails>
                )
              }
            </Transition>
          </SpecialtyRow>
        ))}
        {(nurseSpecialties.size === 0 ||
          (nurseSpecialties.size === 1 &&
            nurseSpecialtiesArr &&
            (nurseSpecialtiesArr.length === 0 ||
              (nurseSpecialtiesArr[0] &&
                nurseSpecialtiesArr[0][0] === undefined)))) && (
          <SpecialtyRow style={{ marginTop: '16px' }}>
            <SpecialtyButtonWrapper $isFullWidth>
              <Tag
                closeable={false}
                variant="solid"
                size="medium"
                kind="custom"
                color={
                  allowNoSpecialties ? theme.colors.primary : 'transparent'
                }
                $textColor={allowNoSpecialties ? 'white' : theme.colors.primary}
                $containerWidth
                onClick={() => {
                  if (allowNoSpecialties) {
                    setAllowNoSpecialties(false);
                  } else {
                    setAllowNoSpecialties(true);
                    if (shouldUpdateRecentGrad) {
                      setOnboardingData({
                        ...onboardingData,
                        recent_grad: true,
                        years_experience: null,
                      });
                    }
                  }
                }}
              >
                No specialties yet
              </Tag>
            </SpecialtyButtonWrapper>
          </SpecialtyRow>
        )}
      </Center>
    );
  }

  return (
    <Onboarding
      currentStep={stepNumber}
      isNextDisabled={Boolean(
        specialtyNeedsValues ||
          (isReactivation &&
            onboardingData.recent_grad &&
            recentGradHasWorkExperience === null)
      )}
      nextAction={
        recentGrad && nextActionForRecentGrads
          ? nextActionForRecentGrads
          : nextAction
      }
      previousAction={previousAction}
      stepKeys={['nurseSpecialties', 'recent_grad', 'years_experience']}
      stepName="Specialties"
    >
      {contents}
    </Onboarding>
  );
};
OnboardingSpecialtiesList.propTypes = {
  hideRecentGradYearEntry: PropTypes.bool,
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  isReactivation: PropTypes.bool,
  nextAction: PropTypes.string,
  nextActionForRecentGrads: PropTypes.string,
  previousAction: PropTypes.string,
  stepNumber: PropTypes.number.isRequired,
};

export default OnboardingSpecialtiesList;
